import React from "react";
import { useTranslation } from "react-i18next";
import { makeStyles, Theme, createStyles } from "@material-ui/core/styles";

import ISubCategory from "../categories/ISubCategory";
import { ProductListView } from "./productList/ProductListView";
import { useAPIV2 } from "../../../yoello-lib/modules/API2/useAPIv2";
import APIExtensions from "../../../yoello-lib/modules/API2/APIDefs";

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    draggableSubcategories: {
      "&.draggable": {
        width: "auto",
        marginTop: "1rem",
      },

      "& .draggable-card": {
        display: "flex",
        marginBottom: "0.9375rem",
        cursor: "move",
      },

      "& .draggable-item": {
        position: "relative",
        padding: "0 2rem",
        lineHeight: "4.375rem",
        backgroundColor: "#ffffff",
        cursor: "pointer",
        transition: "background-color 0.3s",
        boxShadow: "1px 1px 3px rgba(0, 0, 0, 0.2)",
        borderRadius: "0.625rem",
        overflow: "hidden",
        fontSize: "1rem",
        display: "flex !important",
        width: "100%",
        alignItems: "center",
        justifyContent: "space-between",

        "&:hover": {
          top: "-2px",
          left: "-2px",
          boxShadow: "2px 2px 5px rgba(0, 0, 0, 0.2)",
        },

        "& .draggable-enabled": {
          padding: "0.1875rem 0.3125rem",
          borderRadius: "0.25rem",

          "@media only screen and (max-width: 1280px)": {
            position: "unset !important",
            margin: "0 !important",
          },

          "& .enabled": {
            background: "#faf6e0",
            borderRadius: "0.875rem",
            border: "1px solid #ffdd02",
            boxSizing: "border-box",
            borderRradius: "0.875rem",
            fontFamily: "Open Sans",
            fontStyle: "normal",
            fontSize: ".875rem",
            lineHeight: "1.1875rem",
            padding: "0.3125rem 0.625rem",
            display: "flex",
            alignItems: "center",
            textAlign: "center",
            color: "#212121",
          },

          "& .disabled ": {
            background: "#f3f4f9",
            borderRadius: "0.875rem",
            fontFamily: "Open Sans",
            fontStyle: "normal",
            fontSize: ".875rem",
            lineHeight: "1.1875rem",
            display: "flex",
            alignItems: "center",
            textAalign: "center",
            color: "#757575",
            padding: "0.3125rem 0.625rem",
          },
        },
      },

      "& .draggable-handler": {
        width: "4.375rem",
        height: "4.375rem",
        backgroundImage: 'url("/assets/images/icon-handler.svg")',
        backgroundPosition: "center",
        backgroundRepeat: "no-repeat",
      },

      "& .draggable-onDrag": {
        width: "4.375rem",
        height: "4.375rem",
        backgroundImage: 'url("/assets/images/icon-dnd-touch.svg")',
        backgroundPosition: "center",
        backgroundRepeat: "no-repeat",
      },

      "& .draggable-subcategory_name": {
        fontSize: "1rem",
        lineHeight: "1.5",
        width: "calc(30% - 100px)",
      },

      "& .draggable-subcategory_desc": {
        fontSize: "1rem",
        lineHeight: "1.5",
        padding: "0.5rem 0",
        color: "#9e9e9e",
        width: "calc(50% - 100px)",
      },
    },
  })
);

interface ICategoriesProductListViewProps {
  venueId: string;
  onItemSelection: (product: ISubCategory) => void;
  subcategories: ISubCategory[];
  isLoading: boolean;
  filteredSubCategories: ISubCategory[];
  updateSubcategoryPosition: (positions: string[]) => void;
}

export function CategoriesProductListView({
  venueId,
  subcategories,
  onItemSelection,
  isLoading,
  filteredSubCategories,
  updateSubcategoryPosition,
}: ICategoriesProductListViewProps) {

  const { t } = useTranslation();
  const classes = useStyles();

  const { callAPI } = useAPIV2<any>(
    APIExtensions.menuItemSubcategories,
    {
      onMountDisabled: true,
      errorCall: t("menu.subcategories.sortFail"),
      successCall: t("menu.subcategories.sortSuccess"),
      query: "sort",
      yoelloAPIParams: { venue_id: venueId, size: 200, include_disabled: true },
      isPaginated: true
    }
  );

  return (
    <ProductListView
      listClassName={classes.draggableSubcategories}
      isLoading={isLoading}
      productList={filteredSubCategories}
      onItemSelection={onItemSelection}
      sortAPICall={callAPI}
      parentEntry={subcategories}
      emptyEntryText="menu.products.emptySubCat"
      emptyEntryTextInfo="menu.products.emptySubCatInfo"
      emptyListText="menu.products.emptySubCats"
      emptyListTextInfo="menu.products.emptySubCatsInfo"
      fieldList={fields(t)}
      onSortedItem={updateSubcategoryPosition}
    />
  );
}

const fields = (t: any) => [
  {
    key: "subcategory_name",
  },
  {
    key: "subcategory_desc",
  },
  {
    key: "enabled",
    modifier: (enabled: boolean) =>
      enabled ? (
        <span className="enabled">{t("menu.subcategories.enabled")}</span>
      ) : (
        <span className="disabled">{t("menu.subcategories.disabled")}</span>
      ),
  },
];
